import gql from "graphql-tag";
import {useCallback} from "react";

import {useGrowthBookForABTest} from "@khan/analytics-growthbook";
import {useGql} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import type {ExperimentFeatureValue} from "@khan/guide-core-helpers";

import type {AiGuideActivityConfigGrowthbookFeaturesType} from "./__graphql-types__/index.ts";

interface FeatureStore {
    promptVariables: Record<string, string | null>;
    experimentFeatures: Record<string, ExperimentFeatureValue>;
}

export const CONFIG_QUERY: GqlOperation<
    AiGuideActivityConfigGrowthbookFeaturesType["response"],
    AiGuideActivityConfigGrowthbookFeaturesType["variables"]
> = gqlOp(gql`
    query AiGuideActivityConfigGrowthbookFeatures($threadId: String!) {
        aiGuideActivityConfigByThreadId(threadId: $threadId) {
            growthbookFeaturesAsPromptVariables
            growthbookExperimentFeatures
        }
    }
`);

export const useGetGrowthbookFeaturesAsPromptVariables = () => {
    const gqlFetch = useGql();
    const growthBook = useGrowthBookForABTest();

    return useCallback(
        async (threadId: string | null | undefined): Promise<FeatureStore> => {
            if (!growthBook || !threadId) {
                return {
                    promptVariables: {},
                    experimentFeatures: {},
                };
            }

            let aiGuideActivityConfig: AiGuideActivityConfigGrowthbookFeaturesType["response"];
            try {
                aiGuideActivityConfig = await gqlFetch(CONFIG_QUERY, {
                    variables: {threadId},
                });
            } catch (error) {
                return {
                    promptVariables: {},
                    experimentFeatures: {},
                };
            }

            const promptVariableKeys =
                aiGuideActivityConfig?.aiGuideActivityConfigByThreadId
                    ?.growthbookFeaturesAsPromptVariables;

            const experimentFeatureKeys =
                aiGuideActivityConfig?.aiGuideActivityConfigByThreadId
                    ?.growthbookExperimentFeatures;

            if (!promptVariableKeys && !experimentFeatureKeys) {
                return {
                    promptVariables: {},
                    experimentFeatures: {},
                };
            }

            const promptVariableKeysSet = promptVariableKeys
                ? new Set(promptVariableKeys)
                : new Set<string>();
            const experimentFeatureKeysSet = experimentFeatureKeys
                ? new Set(experimentFeatureKeys)
                : new Set<string>();

            // Merging these from the start ensures we only enroll a thread
            // once for each feature name but that feature can be used
            // with componentOverrides AND directly in prompt variables
            const growthbookFeatureKeys = new Set([
                ...promptVariableKeysSet,
                ...experimentFeatureKeysSet,
            ]);

            // Add threadId context to GrowthBook Attributes.
            growthBook?.setAttributes({
                ...growthBook.getAttributes(),
                threadId,
            });

            const promptVariables: Record<string, string | null> = {};
            const experimentFeatures: Record<string, ExperimentFeatureValue> =
                {};

            for (const featureKey of growthbookFeatureKeys) {
                const rawFeatureValue =
                    growthBook.getFeatureValueAndEnrollInABTest(
                        featureKey,
                        "NONE",
                    );
                if (experimentFeatureKeysSet.has(featureKey)) {
                    experimentFeatures[featureKey] = rawFeatureValue;
                }

                // Transform the feature key using kebab-case to camelCase.
                // Prompt Variables are required to be in camelCase
                const safeFeatureKey = featureKey.replace(
                    /-([a-z])/g,
                    (_, letter) => letter.toUpperCase(),
                );

                const stringValue =
                    getPromptVariableStringValue(rawFeatureValue);
                promptVariables[safeFeatureKey] = stringValue;
            }

            return {
                promptVariables: promptVariables,
                experimentFeatures: experimentFeatures,
            };
        },
        [gqlFetch, growthBook],
    );
};

// Be defensive: ensure we always pass a string (or null) to the
// prompt variables API.
const getPromptVariableStringValue = (rawFeatureValue: any): string | null => {
    let stringValue: string | null;
    if (!rawFeatureValue || rawFeatureValue === "NONE") {
        stringValue = null;
    } else if (typeof rawFeatureValue === "object") {
        try {
            stringValue = JSON.stringify(rawFeatureValue);
        } catch {
            stringValue = String(rawFeatureValue);
        }
    } else {
        stringValue = String(rawFeatureValue);
    }

    return stringValue;
};
